import { useSyncExternalStore } from 'react';

import { currentWorkspaceId } from './meta';

// Wouter params + RouteWorkspaceProvider are the source of truth inside the
// routed React tree, but some consumers live outside that tree entirely:
// singleton stores, Stimulus-mounted debug UI, and any other code that cannot
// call a React hook or receive provider context. Those consumers still need a
// push signal when a client-side workspace switch happens.
//
// `lib/meta.js` is enough for one-off "what is the workspace right now?" reads,
// but it is intentionally non-reactive. This module provides the reactive
// bridge for out-of-tree code: the provider writes the latest ids into meta and
// dispatches this event after every routed scope change, while subscribers here
// can stay in sync without depending on DOM MutationObservers or wouter internals.
export const WORKSPACE_ID_SCOPE_EVENT = 'output:internal:workspace-id-scope';

// `useSyncExternalStore` needs a pull snapshot for initial render and for any
// missed event edge cases. The meta tag remains the canonical shared snapshot
// for code outside the provider tree, so read from it directly here.
const getSnapshot = () => currentWorkspaceId();
const getServerSnapshot = () => null;
const subscribe = (callback) => subscribeWorkspaceIdScope(() => callback());

export const dispatchWorkspaceIdScope = ({ workspaceId = currentWorkspaceId(), profileId = null } = {}) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(WORKSPACE_ID_SCOPE_EVENT, {
      detail: {
        workspaceId,
        profileId,
      },
    }),
  );
};

// Subscribers get both the normalized workspace id and the full event detail.
// Falling back to `currentWorkspaceId()` keeps late subscribers and defensive
// callers correct even if they attach between the meta write and event delivery.
export const subscribeWorkspaceIdScope = (callback) => {
  if (typeof window === 'undefined') return () => {};

  const handler = (event) => callback(event?.detail?.workspaceId ?? currentWorkspaceId(), event?.detail ?? {});
  window.addEventListener(WORKSPACE_ID_SCOPE_EVENT, handler);
  return () => window.removeEventListener(WORKSPACE_ID_SCOPE_EVENT, handler);
};

// Hook form for React code that is still outside RouteWorkspaceProvider
// (currently the log viewer). Anything inside the routed tree should keep using
// `useRouteWorkspace()` directly instead of layering through this event bridge.
export const useWorkspaceIdScope = () => {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
